import React, { useState, useEffect } from 'react';
import { 
  CheckCircle, 
  XCircle, 
  RefreshCw, 
  Link as LinkIcon, 
  Settings,
  Music,
  Mail,
  Phone,
  Cloud,
  Calendar,
  Users,
  Video,
  Camera,
  MessageCircle,
  Tv,
  ExternalLink,
  X,
  Info,
  ArrowRight,
  Copy,
  Check
} from 'lucide-react';

const MyApps = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectingApp, setConnectingApp] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const apiUrl = typeof window !== 'undefined' ? window.location.origin : '';

  // Modal states
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [showICloudModal, setShowICloudModal] = useState(false);
  const [showTwilioModal, setShowTwilioModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [icloudEmail, setIcloudEmail] = useState('');
  const [icloudPassword, setIcloudPassword] = useState('');
  const [twilioAccountSid, setTwilioAccountSid] = useState('');
  const [twilioAuthToken, setTwilioAuthToken] = useState('');
  const [twilioPhoneNumber, setTwilioPhoneNumber] = useState('');
  const [copied, setCopied] = useState(false);

  // Nylas Connected Accounts state
  const [nylasAccounts, setNylasAccounts] = useState([]);
  const [nylasLoading, setNylasLoading] = useState(false);
  const [showNylasModal, setShowNylasModal] = useState(false);
  const [newAccountName, setNewAccountName] = useState('');
  const [nylasConnecting, setNylasConnecting] = useState(false);
  const [disconnectingEmail, setDisconnectingEmail] = useState(null);
  const userId = 'dwoodson';

  // Fetch Nylas connected accounts
  const fetchNylasAccounts = async () => {
    try {
      setNylasLoading(true);
      const response = await fetch(`${apiUrl}/api/nylas/connected-summary?user_id=${userId}`);
      const data = await response.json();
      if (data.accounts) {
        setNylasAccounts(data.accounts);
      }
    } catch (err) {
      console.error('Error fetching Nylas accounts:', err);
    } finally {
      setNylasLoading(false);
    }
  };

  // Connect new Nylas account
  const handleConnectNylasAccount = () => {
    const accountName = newAccountName.trim() || 'My Account';
    setNylasConnecting(true);
    window.location.href = `${apiUrl}/api/nylas/auth/connect?user_id=${userId}&account_name=${encodeURIComponent(accountName)}&redirect_to=my-apps`;
  };

  // Disconnect Nylas account
  const handleDisconnectNylas = async (email) => {
    if (!window.confirm(`Disconnect ${email}? This will remove access to email, calendar, and contacts for this account.`)) {
      return;
    }
    
    try {
      setDisconnectingEmail(email);
      const response = await fetch(
        `${apiUrl}/api/nylas/disconnect?user_id=${userId}&email=${encodeURIComponent(email)}`,
        { method: 'DELETE' }
      );
      
      if (response.ok) {
        setNylasAccounts(prev => prev.filter(acc => acc.email !== email));
      }
    } catch (err) {
      console.error('Failed to disconnect account:', err);
    } finally {
      setDisconnectingEmail(null);
    }
  };

  // Get provider icon for Nylas accounts
  const getNylasProviderIcon = (provider) => {
    switch (provider?.toLowerCase()) {
      case 'google':
        return (
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
        );
      case 'microsoft':
      case 'outlook':
        return (
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#F25022" d="M1 1h10v10H1z"/>
            <path fill="#00A4EF" d="M1 13h10v10H1z"/>
            <path fill="#7FBA00" d="M13 1h10v10H13z"/>
            <path fill="#FFB900" d="M13 13h10v10H13z"/>
          </svg>
        );
      case 'yahoo':
        return (
          <div className="w-5 h-5 bg-purple-600 rounded flex items-center justify-center text-white text-xs font-bold">Y</div>
        );
      default:
        return <Mail className="w-5 h-5 text-gray-400" />;
    }
  };

  // Fetch connection status for all apps
  const checkConnectionStatus = async () => {
    // Check Gmail status - get all connected accounts
    let gmailConnected = false;
    let gmailAccounts = [];
    try {
      const gmailResponse = await fetch(`${apiUrl}/api/gmail/accounts`);
      if (gmailResponse.ok) {
        const gmailData = await gmailResponse.json();
        gmailAccounts = gmailData.accounts || [];
        gmailConnected = gmailAccounts.some(acc => acc.connected);
      }
    } catch (e) {
      console.log('Gmail accounts check failed:', e);
    }

    // Check Plex status
    let plexConnected = false;
    let plexServerName = '';
    try {
      const plexResponse = await fetch(`${apiUrl}/api/plex/status`);
      if (plexResponse.ok) {
        const plexData = await plexResponse.json();
        plexConnected = plexData.status === 'connected';
        plexServerName = plexData.server_name || '';
      }
    } catch (e) {
      console.log('Plex status check failed:', e);
    }

    // Check Spotify status
    let spotifyConnected = localStorage.getItem('spotify_access_token') ? true : false;

    // Check Instagram status
    let instagramConnected = false;
    let instagramUsername = '';
    try {
      const igResponse = await fetch(`${apiUrl}/api/instagram/status`);
      if (igResponse.ok) {
        const igData = await igResponse.json();
        instagramConnected = igData.connected === true;
        instagramUsername = igData.account?.username || '';
      }
    } catch (e) {
      console.log('Instagram status check failed:', e);
    }

    // Check Facebook status
    let facebookConnected = false;
    let facebookPageName = '';
    try {
      const fbResponse = await fetch(`${apiUrl}/api/facebook/status`);
      if (fbResponse.ok) {
        const fbData = await fbResponse.json();
        facebookConnected = fbData.page_connected === true;
        facebookPageName = fbData.page_name || '';
      }
    } catch (e) {
      console.log('Facebook status check failed:', e);
    }

    // Check iCloud contacts
    let icloudContactsConnected = false;
    let icloudContactsCount = 0;
    try {
      const contactsResponse = await fetch(`${apiUrl}/api/icloud-contacts/stored?user_id=dwoodson`);
      if (contactsResponse.ok) {
        const contactsData = await contactsResponse.json();
        icloudContactsConnected = contactsData.contacts && contactsData.contacts.length > 0;
        icloudContactsCount = contactsData.contacts ? contactsData.contacts.length : 0;
      }
    } catch (e) {
      console.log('iCloud contacts status check failed:', e);
    }

    // Check Stocks API status
    let stocksConnected = false;
    try {
      const stocksResponse = await fetch(`${apiUrl}/api/stocks/status`);
      if (stocksResponse.ok) {
        const stocksData = await stocksResponse.json();
        stocksConnected = stocksData.api_configured === true;
      }
    } catch (e) {
      console.log('Stocks status check failed:', e);
    }

    // Check Twilio SMS/Voice status
    let twilioSmsConnected = false;
    let twilioVoiceConnected = false;
    let twilioPhoneNum = '';
    try {
      const twilioResponse = await fetch(`${apiUrl}/api/twilio/status`);
      if (twilioResponse.ok) {
        const twilioData = await twilioResponse.json();
        twilioSmsConnected = twilioData.sms_connected === true;
        twilioVoiceConnected = twilioData.voice_connected === true;
        twilioPhoneNum = twilioData.phone_number || '';
      }
    } catch (e) {
      console.log('Twilio status check failed:', e);
    }

    // Check iCloud Calendar status
    let icloudCalendarConnected = false;
    let icloudCalendarEmail = '';
    try {
      const calResponse = await fetch(`${apiUrl}/api/icloud-calendar/config/dwoodson`);
      if (calResponse.ok) {
        const calData = await calResponse.json();
        icloudCalendarConnected = calData.configured === true && calData.is_active === true;
        icloudCalendarEmail = calData.apple_id || '';
      }
    } catch (e) {
      console.log('iCloud Calendar status check failed:', e);
    }

    // Check YouTube status
    let youtubeConnected = false;
    try {
      const ytResponse = await fetch(`${apiUrl}/api/youtube/status`);
      if (ytResponse.ok) {
        const ytData = await ytResponse.json();
        youtubeConnected = ytData.connected === true;
      }
    } catch (e) {
      console.log('YouTube status check failed:', e);
    }

    const connectedGmailCount = gmailAccounts.filter(a => a.connected).length;
    const gmailDescription = gmailConnected 
      ? `${connectedGmailCount} account${connectedGmailCount > 1 ? 's' : ''} connected`
      : 'Google email service';

    const appsList = [
      // Music & Entertainment
      {
        id: 'spotify',
        name: 'Spotify',
        description: spotifyConnected ? 'Connected' : 'Music streaming and playback',
        category: 'Music & Entertainment',
        icon: Music,
        color: '#1DB954',
        connected: spotifyConnected,
        connectType: 'oauth',
        authUrl: `${apiUrl}/api/spotify/auth/login`,
        instructions: {
          title: 'Connect Spotify',
          steps: [
            'Click "Connect with Spotify" below',
            'Log in to your Spotify account',
            'Authorize the app to access your music',
            'You\'ll be redirected back automatically'
          ],
          note: 'Requires a Spotify account (Free or Premium)',
          destinationPage: '/media-factory'
        }
      },
      {
        id: 'plex',
        name: 'Plex',
        description: plexConnected && plexServerName ? plexServerName : 'Media server for movies and TV shows',
        category: 'Music & Entertainment',
        icon: Tv,
        color: '#E5A00D',
        connected: plexConnected,
        connectType: 'configured',
        instructions: {
          title: 'Plex Media Server',
          steps: [
            'Plex is pre-configured for this dashboard',
            'Your media server is automatically detected',
            'Movies and TV shows sync in real-time',
            'Visit Media Factory to browse your library'
          ],
          note: 'Server configured via environment variables',
          destinationPage: '/media-factory'
        }
      },
      // Email
      {
        id: 'gmail',
        name: 'Gmail',
        description: gmailDescription,
        category: 'Email',
        icon: Mail,
        color: '#EA4335',
        connected: gmailConnected,
        connectType: 'oauth',
        authUrl: `${apiUrl}/api/gmail/auth`,
        connectedAccounts: gmailAccounts.filter(a => a.connected),
        instructions: {
          title: 'Connect Gmail',
          steps: [
            'Click "Connect with Google" below',
            'Select your Google account',
            'Grant permission to read and send emails',
            'Your emails will sync to Communications'
          ],
          note: 'You can connect multiple Gmail accounts',
          destinationPage: '/communications'
        }
      },
      {
        id: 'outlook',
        name: 'Outlook',
        description: 'Microsoft email and calendar',
        category: 'Email',
        icon: Mail,
        color: '#0078D4',
        connected: false,
        connectType: 'oauth',
        authUrl: `${apiUrl}/api/outlook/auth`,
        instructions: {
          title: 'Connect Outlook',
          steps: [
            'Click "Connect with Microsoft" below',
            'Sign in with your Microsoft account',
            'Allow access to your email and calendar',
            'Outlook data syncs to Communications'
          ],
          note: 'Works with Outlook.com and Microsoft 365',
          destinationPage: '/communications'
        }
      },
      {
        id: 'icloud_mail',
        name: 'iCloud Mail',
        description: 'Apple email service',
        category: 'Email',
        icon: Mail,
        color: '#007AFF',
        connected: localStorage.getItem('icloud_mail_connected') === 'true',
        connectType: 'credentials',
        modalType: 'icloud_mail',
        instructions: {
          title: 'Connect iCloud Mail',
          steps: [
            'Go to appleid.apple.com and sign in',
            'Navigate to Sign-In and Security',
            'Generate an App-Specific Password',
            'Enter your Apple ID and the password below'
          ],
          note: 'Requires an app-specific password for security',
          destinationPage: '/communications'
        }
      },
      // Calendar
      {
        id: 'google_calendar',
        name: 'Google Calendar',
        description: 'Google calendar service',
        category: 'Calendar',
        icon: Calendar,
        color: '#4285F4',
        connected: localStorage.getItem('calendar_connected_type') === 'google',
        connectType: 'navigate',
        navigateTo: '/calendar-re',
        instructions: {
          title: 'Connect Google Calendar',
          steps: [
            'Go to the Calendar page',
            'Click "Connect Google Calendar"',
            'Sign in with your Google account',
            'Your events will sync automatically'
          ],
          note: 'Events appear on your dashboard calendar',
          destinationPage: '/calendar-re'
        }
      },
      {
        id: 'outlook_calendar',
        name: 'Outlook Calendar',
        description: 'Microsoft calendar service',
        category: 'Calendar',
        icon: Calendar,
        color: '#0078D4',
        connected: false,
        connectType: 'navigate',
        navigateTo: '/calendar-re',
        instructions: {
          title: 'Connect Outlook Calendar',
          steps: [
            'Go to the Calendar page',
            'Click "Connect Outlook Calendar"',
            'Sign in with Microsoft',
            'Calendar events sync automatically'
          ],
          note: 'Works with Outlook.com and Microsoft 365',
          destinationPage: '/calendar-re'
        }
      },
      {
        id: 'icloud_calendar',
        name: 'iCloud Calendar',
        description: icloudCalendarConnected && icloudCalendarEmail ? icloudCalendarEmail : 'Apple calendar service',
        category: 'Calendar',
        icon: Calendar,
        color: '#007AFF',
        connected: icloudCalendarConnected,
        connectType: icloudCalendarConnected ? 'navigate' : 'credentials',
        navigateTo: '/calendar-re',
        modalType: 'icloud_calendar',
        instructions: {
          title: 'Connect iCloud Calendar',
          steps: icloudCalendarConnected ? [
            'Your iCloud Calendar is connected',
            'Events sync automatically',
            'Go to Calendar page to view events'
          ] : [
            'Generate an App-Specific Password at appleid.apple.com',
            'Go to Sign-In and Security → App-Specific Passwords',
            'Create a new password for "Dashboard Calendar"',
            'Enter credentials in the form below'
          ],
          note: icloudCalendarConnected ? 'Calendar events synced from iCloud' : 'Your Apple ID and app-specific password are required',
          destinationPage: '/calendar-re'
        }
      },
      // Contacts
      {
        id: 'google_contacts',
        name: 'Google Contacts',
        description: 'Google contacts sync',
        category: 'Contacts',
        icon: Users,
        color: '#34A853',
        connected: localStorage.getItem('google_contacts_connected') === 'true',
        connectType: 'navigate',
        navigateTo: '/contacts-re',
        instructions: {
          title: 'Connect Google Contacts',
          steps: [
            'Go to the Contacts page',
            'Click "Add Account" → Google',
            'Authorize access to your contacts',
            'Contacts import automatically'
          ],
          note: 'Your Google contacts sync in real-time',
          destinationPage: '/contacts-re'
        }
      },
      {
        id: 'outlook_contacts',
        name: 'Outlook Contacts',
        description: 'Microsoft contacts sync',
        category: 'Contacts',
        icon: Users,
        color: '#0078D4',
        connected: localStorage.getItem('outlook_contacts_connected') === 'true',
        connectType: 'navigate',
        navigateTo: '/contacts-re',
        instructions: {
          title: 'Connect Outlook Contacts',
          steps: [
            'Go to the Contacts page',
            'Click "Add Account" → Outlook',
            'Sign in with Microsoft',
            'Contacts import automatically'
          ],
          note: 'Works with Outlook.com and Microsoft 365',
          destinationPage: '/contacts-re'
        }
      },
      {
        id: 'icloud_contacts',
        name: 'iCloud Contacts',
        description: icloudContactsConnected ? `${icloudContactsCount.toLocaleString()} contacts synced` : 'Apple contacts sync',
        category: 'Contacts',
        icon: Users,
        color: '#007AFF',
        connected: icloudContactsConnected,
        connectType: 'navigate',
        navigateTo: '/contacts-re',
        instructions: {
          title: 'Connect iCloud Contacts',
          steps: [
            'Go to the Contacts page',
            'Click "Connect iCloud"',
            'Enter your Apple ID credentials',
            'Generate an app-specific password',
            'Contacts will sync from iCloud'
          ],
          note: 'Requires app-specific password from appleid.apple.com',
          destinationPage: '/contacts-re'
        }
      },
      // Messaging & Calls
      {
        id: 'twilio_sms',
        name: 'Twilio SMS',
        description: twilioSmsConnected && twilioPhoneNum ? twilioPhoneNum : 'SMS messaging service',
        category: 'Messaging & Calls',
        icon: MessageCircle,
        color: '#F22F46',
        connected: twilioSmsConnected,
        connectType: twilioSmsConnected ? 'navigate' : 'credentials',
        navigateTo: '/communications',
        modalType: 'twilio_sms',
        instructions: {
          title: twilioSmsConnected ? 'Twilio SMS Connected' : 'Connect Twilio SMS',
          steps: twilioSmsConnected ? [
            'Twilio SMS is configured and ready',
            `Phone number: ${twilioPhoneNum}`,
            'Go to Communications to send messages'
          ] : [
            'Create a Twilio account at twilio.com',
            'Get your Account SID from the console',
            'Get your Auth Token',
            'Purchase or use a Twilio phone number',
            'Enter the credentials below'
          ],
          note: twilioSmsConnected ? 'Send SMS from Communications page' : 'Twilio offers a free trial with test credits',
          destinationPage: '/communications'
        }
      },
      {
        id: 'twilio_voice',
        name: 'Twilio Voice',
        description: twilioVoiceConnected && twilioPhoneNum ? twilioPhoneNum : 'Voice calling service',
        category: 'Messaging & Calls',
        icon: Phone,
        color: '#F22F46',
        connected: twilioVoiceConnected,
        connectType: twilioVoiceConnected ? 'navigate' : 'credentials',
        navigateTo: '/communications',
        modalType: 'twilio_voice',
        instructions: {
          title: twilioVoiceConnected ? 'Twilio Voice Connected' : 'Connect Twilio Voice',
          steps: twilioVoiceConnected ? [
            'Twilio Voice is configured and ready',
            `Phone number: ${twilioPhoneNum}`,
            'Go to Communications to make calls'
          ] : [
            'Create a Twilio account at twilio.com',
            'Enable Voice capabilities',
            'Get your Account SID and Auth Token',
            'Get a voice-enabled phone number',
            'Enter credentials below'
          ],
          note: twilioVoiceConnected ? 'Make calls from Communications page' : 'Same credentials as Twilio SMS if already connected',
          destinationPage: '/communications'
        }
      },
      {
        id: 'iphone_messages',
        name: 'iPhone Messages',
        description: 'iMessage via Shortcuts',
        category: 'Messaging & Calls',
        icon: MessageCircle,
        color: '#34C759',
        connected: localStorage.getItem('messaging_connection_type') === 'shortcuts',
        connectType: 'navigate',
        navigateTo: '/communications',
        instructions: {
          title: 'Connect iPhone Messages',
          steps: [
            'Go to Communications → Messages tab',
            'Click "Connect iPhone via Shortcuts"',
            'Download the Shortcut to your iPhone',
            'Run the Shortcut to sync messages',
            'Messages appear in the dashboard'
          ],
          note: 'Requires iPhone with Shortcuts app',
          destinationPage: '/communications'
        }
      },
      {
        id: 'iphone_calls',
        name: 'iPhone Calls',
        description: 'Call logs via Shortcuts',
        category: 'Messaging & Calls',
        icon: Phone,
        color: '#34C759',
        connected: localStorage.getItem('calls_connection_type') === 'shortcuts',
        connectType: 'navigate',
        navigateTo: '/communications',
        instructions: {
          title: 'Connect iPhone Calls',
          steps: [
            'Go to Communications → Calls tab',
            'Click "Connect iPhone via Shortcuts"',
            'Download the Shortcut',
            'Run to sync your call history',
            'Call logs appear in dashboard'
          ],
          note: 'Requires iPhone with Shortcuts app',
          destinationPage: '/communications'
        }
      },
      // Social Media
      {
        id: 'instagram',
        name: 'Instagram',
        description: instagramConnected && instagramUsername ? `@${instagramUsername}` : 'Photo and video sharing',
        category: 'Social Media',
        icon: Camera,
        color: '#E4405F',
        connected: instagramConnected,
        connectType: 'oauth',
        authUrl: `${apiUrl}/api/instagram/auth`,
        instructions: {
          title: 'Connect Instagram',
          steps: [
            'Click "Connect with Instagram" below',
            'Log in to your Instagram account',
            'Authorize the app',
            'View analytics in Social Media page'
          ],
          note: 'Requires Instagram Business or Creator account',
          destinationPage: '/social-media'
        }
      },
      {
        id: 'facebook',
        name: 'Facebook',
        description: facebookConnected && facebookPageName ? facebookPageName : 'Social networking',
        category: 'Social Media',
        icon: Users,
        color: '#1877F2',
        connected: facebookConnected,
        connectType: 'oauth',
        authUrl: `${apiUrl}/api/facebook/auth`,
        instructions: {
          title: 'Connect Facebook',
          steps: [
            'Click "Connect with Facebook" below',
            'Log in to Facebook',
            'Select the Page to connect',
            'Grant page management permissions',
            'View analytics in Social Media page'
          ],
          note: 'Requires a Facebook Page (not personal profile)',
          destinationPage: '/social-media'
        }
      },
      {
        id: 'youtube',
        name: 'YouTube',
        description: youtubeConnected ? 'Video search & analytics' : 'Video platform',
        category: 'Social Media',
        icon: Video,
        color: '#FF0000',
        connected: youtubeConnected,
        connectType: youtubeConnected ? 'navigate' : 'configured',
        navigateTo: '/social-media',
        instructions: {
          title: youtubeConnected ? 'YouTube Connected' : 'YouTube Integration',
          steps: youtubeConnected ? [
            'YouTube API is connected and ready',
            'Search for videos and channels',
            'View video analytics and details',
            'Go to Social Media page to explore'
          ] : [
            'YouTube integration requires API key',
            'Contact admin to configure',
            'Will support video search',
            'Channel analytics available'
          ],
          note: youtubeConnected ? 'Search videos from Social Media page' : 'API key configured in environment',
          destinationPage: '/social-media'
        }
      },
      // Finance
      {
        id: 'alpha_vantage',
        name: 'Stock Market',
        description: stocksConnected ? 'Real-time quotes' : 'Stock market data',
        category: 'Finance',
        icon: ({ size, style }) => (
          <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={style}>
            <path d="M3 3v18h18" />
            <path d="M18 9l-5 5-4-4-3 3" />
          </svg>
        ),
        color: '#10B981',
        connected: stocksConnected,
        connectType: 'configured',
        instructions: {
          title: 'Stock Market Data',
          steps: [
            'Stock data is powered by Alpha Vantage',
            'Real-time quotes and charts available',
            'Go to Investments → Stocks tab',
            'Add stocks to your watchlist'
          ],
          note: 'API configured via environment variables',
          destinationPage: '/investments-re'
        }
      }
    ];
    
    setApps(appsList);
    setLoading(false);
    setRefreshing(false);
  };

  useEffect(() => {
    checkConnectionStatus();
    fetchNylasAccounts();
    
    // Check URL params for Nylas OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('nylas_connected') === 'true') {
      window.history.replaceState({}, '', '/my-apps');
      fetchNylasAccounts();
    } else if (urlParams.get('nylas_error') === 'true') {
      window.history.replaceState({}, '', '/my-apps');
    }
  }, [apiUrl]);

  const handleRefresh = () => {
    setRefreshing(true);
    checkConnectionStatus();
    fetchNylasAccounts();
  };

  const handleConnect = (app) => {
    setSelectedApp(app);
    
    if (app.connectType === 'credentials') {
      if (app.modalType.includes('icloud')) {
        setModalType(app.modalType);
        setShowICloudModal(true);
      } else if (app.modalType.includes('twilio')) {
        setModalType(app.modalType);
        setShowTwilioModal(true);
      }
    } else if (app.connectType === 'coming_soon') {
      setShowConnectionModal(true);
    } else {
      setShowConnectionModal(true);
    }
  };

  const handleConnectAction = () => {
    if (!selectedApp) return;
    
    setConnectingApp(selectedApp.id);
    setShowConnectionModal(false);
    
    if (selectedApp.connectType === 'oauth' && selectedApp.authUrl) {
      window.location.href = selectedApp.authUrl;
    } else if (selectedApp.connectType === 'navigate' && selectedApp.navigateTo) {
      window.location.href = selectedApp.navigateTo;
    } else if (selectedApp.connectType === 'configured') {
      window.location.href = selectedApp.instructions?.destinationPage || '/';
    }
  };

  const handleDisconnect = (appId) => {
    const keysToRemove = [
      `${appId}_connected`,
      `${appId}_token`,
      `${appId}_email`,
    ];
    
    if (appId === 'gmail') {
      localStorage.removeItem('gmailEmail');
      localStorage.removeItem('gmail_connected');
    } else if (appId === 'spotify') {
      localStorage.removeItem('spotify_connected');
      localStorage.removeItem('spotify_access_token');
    } else if (appId === 'twilio_sms') {
      localStorage.removeItem('messaging_connection_type');
      localStorage.removeItem('messaging_connected');
    } else if (appId === 'twilio_voice') {
      localStorage.removeItem('calls_connection_type');
      localStorage.removeItem('calls_connected');
    } else if (appId === 'iphone_messages') {
      localStorage.removeItem('messaging_connection_type');
      localStorage.removeItem('messaging_connected');
    } else if (appId === 'iphone_calls') {
      localStorage.removeItem('calls_connection_type');
      localStorage.removeItem('calls_connected');
    } else if (appId.includes('calendar')) {
      localStorage.removeItem('calendar_connected_type');
    } else if (appId === 'icloud_mail') {
      localStorage.removeItem('icloud_mail_connected');
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    setApps(prev => prev.map(app => 
      app.id === appId ? { ...app, connected: false } : app
    ));
  };

  const handleICloudConnect = () => {
    if (!icloudEmail || !icloudPassword) {
      alert('Please enter your Apple ID and app-specific password');
      return;
    }
    
    localStorage.setItem(`${modalType}_email`, icloudEmail);
    localStorage.setItem(`${modalType}_connected`, 'true');
    
    if (modalType === 'icloud_calendar') {
      localStorage.setItem('calendar_connected_type', 'icloud');
    }
    
    setApps(prev => prev.map(app => 
      app.id === modalType || app.modalType === modalType ? { ...app, connected: true } : app
    ));
    
    setShowICloudModal(false);
    setIcloudEmail('');
    setIcloudPassword('');
    
    // Navigate to destination
    const app = apps.find(a => a.modalType === modalType);
    if (app?.instructions?.destinationPage) {
      window.location.href = app.instructions.destinationPage;
    }
  };

  const handleTwilioConnect = () => {
    if (!twilioAccountSid || !twilioAuthToken || !twilioPhoneNumber) {
      alert('Please fill in all Twilio credentials');
      return;
    }
    
    localStorage.setItem('twilio_account_sid', twilioAccountSid);
    localStorage.setItem('twilio_auth_token', twilioAuthToken);
    localStorage.setItem('twilio_phone_number', twilioPhoneNumber);
    
    if (modalType === 'twilio_sms') {
      localStorage.setItem('messaging_connected', 'true');
      localStorage.setItem('messaging_connection_type', 'twilio');
    } else if (modalType === 'twilio_voice') {
      localStorage.setItem('calls_connected', 'true');
      localStorage.setItem('calls_connection_type', 'twilio');
    }
    
    setApps(prev => prev.map(app => 
      app.id === modalType ? { ...app, connected: true } : app
    ));
    
    setShowTwilioModal(false);
    setTwilioAccountSid('');
    setTwilioAuthToken('');
    setTwilioPhoneNumber('');
    
    window.location.href = '/communications';
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Group apps by category
  const groupedApps = apps.reduce((acc, app) => {
    if (!acc[app.category]) {
      acc[app.category] = [];
    }
    acc[app.category].push(app);
    return acc;
  }, {});

  const connectedCount = apps.filter(app => app.connected).length;
  const totalCount = apps.length;

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black p-3 sm:p-4 md:p-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-1 sm:mb-2">My Apps</h1>
            <p className="text-gray-400 text-sm sm:text-base">
              Manage your connected apps and services
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors disabled:opacity-50 text-sm sm:text-base self-start sm:self-auto"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
        <div className="mt-3 sm:mt-4 flex flex-wrap items-center gap-2 sm:gap-4">
          <div className="flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-green-500/10 border border-green-500/30 rounded-lg">
            <CheckCircle className="text-green-400" size={16} />
            <span className="text-green-400 text-sm sm:text-base font-medium">{connectedCount + nylasAccounts.length} Connected</span>
          </div>
          <div className="flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-gray-800 border border-gray-700 rounded-lg">
            <XCircle className="text-gray-400" size={16} />
            <span className="text-gray-400 text-sm sm:text-base font-medium">{totalCount - connectedCount} Not Connected</span>
          </div>
        </div>
      </div>

      {/* Connected Email Accounts Section (Nylas) */}
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 mb-4">
          <h2 className="text-base sm:text-lg font-semibold text-white flex items-center gap-2">
            <Mail className="text-blue-400" size={18} />
            Connected Email Accounts
            <span className="text-xs text-gray-500 font-normal">
              ({nylasAccounts.length} account{nylasAccounts.length !== 1 ? 's' : ''})
            </span>
          </h2>
          <button
            onClick={() => setShowNylasModal(true)}
            className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm self-start sm:self-auto"
            data-testid="add-email-account-btn"
          >
            <LinkIcon size={16} />
            Add Email Account
          </button>
        </div>

        {nylasLoading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
          </div>
        ) : nylasAccounts.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {nylasAccounts.map((account) => (
              <div
                key={account.email}
                className="bg-gray-900 border border-green-500/30 rounded-xl p-4 hover:border-green-500/50 transition-all"
                data-testid={`nylas-account-${account.email}`}
              >
                {/* Connection Status Badge */}
                <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                  Connected
                </div>

                {/* Account Info */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-gray-800 rounded-lg">
                    {getNylasProviderIcon(account.provider)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-semibold truncate">{account.name || 'Email Account'}</h3>
                    <p className="text-xs text-gray-400 truncate">{account.email}</p>
                  </div>
                </div>

                {/* Features */}
                <div className="flex flex-wrap gap-1 mb-3">
                  {account.features?.email && (
                    <span className="px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded text-xs">Email</span>
                  )}
                  {account.features?.calendar && (
                    <span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded text-xs">Calendar</span>
                  )}
                  {account.features?.contacts && (
                    <span className="px-2 py-0.5 bg-green-500/20 text-green-300 rounded text-xs">Contacts</span>
                  )}
                </div>

                {/* Provider Badge */}
                <div className="text-xs text-gray-500 capitalize mb-3">
                  via {account.provider || 'Email'}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleDisconnectNylas(account.email)}
                    disabled={disconnectingEmail === account.email}
                    className="flex-1 px-3 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1 disabled:opacity-50"
                    data-testid={`disconnect-nylas-${account.email}`}
                  >
                    {disconnectingEmail === account.email ? (
                      <RefreshCw size={14} className="animate-spin" />
                    ) : (
                      <XCircle size={14} />
                    )}
                    Disconnect
                  </button>
                  <button
                    onClick={() => window.location.href = '/communications'}
                    className="px-3 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg text-sm font-medium transition-colors"
                    title="Open Communications"
                  >
                    <ExternalLink size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center">
            <Mail className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <h3 className="text-white font-semibold mb-2">No Email Accounts Connected</h3>
            <p className="text-gray-400 text-sm mb-4">
              Connect your email accounts to sync emails, calendars, and contacts.
            </p>
            <button
              onClick={() => setShowNylasModal(true)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
            >
              Connect Email Account
            </button>
          </div>
        )}
      </div>

      {/* Apps Grid by Category */}
      <div className="space-y-8">
        {Object.entries(groupedApps).map(([category, categoryApps]) => (
          <div key={category}>
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              {category}
              <span className="text-xs text-gray-500 font-normal">
                ({categoryApps.filter(a => a.connected).length}/{categoryApps.length} connected)
              </span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {categoryApps.map((app) => {
                const IconComponent = app.icon;
                return (
                  <div
                    key={app.id}
                    data-testid={`app-card-${app.id}`}
                    className={`relative bg-gray-900 border rounded-xl p-4 transition-all hover:border-gray-600 ${
                      app.connected ? 'border-green-500/30' : 'border-gray-800'
                    }`}
                  >
                    {/* Connection Status Badge */}
                    <div className={`absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                      app.connected 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-gray-800 text-gray-500'
                    }`}>
                      {app.connected ? (
                        <>
                          <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                          Connected
                        </>
                      ) : (
                        <>
                          <span className="w-1.5 h-1.5 bg-gray-500 rounded-full" />
                          Not Connected
                        </>
                      )}
                    </div>

                    {/* App Icon & Name */}
                    <div className="flex items-center gap-3 mb-3">
                      <div 
                        className="p-2 rounded-lg"
                        style={{ backgroundColor: `${app.color}20` }}
                      >
                        <IconComponent 
                          size={24} 
                          style={{ color: app.color }}
                        />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold">{app.name}</h3>
                        <p className="text-xs text-gray-500">{app.description}</p>
                      </div>
                    </div>

                    {/* Connected Accounts List (for Gmail) */}
                    {app.connectedAccounts && app.connectedAccounts.length > 0 && (
                      <div className="mb-3 space-y-1">
                        {app.connectedAccounts.slice(0, 3).map((acc, idx) => (
                          <div key={idx} className="text-xs text-gray-400 flex items-center gap-1">
                            <CheckCircle size={10} className="text-green-400" />
                            {acc.email}
                          </div>
                        ))}
                        {app.connectedAccounts.length > 3 && (
                          <div className="text-xs text-gray-500">
                            +{app.connectedAccounts.length - 3} more
                          </div>
                        )}
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-2 mt-4">
                      {app.connected ? (
                        <>
                          <button
                            onClick={() => handleDisconnect(app.id)}
                            data-testid={`disconnect-${app.id}`}
                            className="flex-1 px-3 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1"
                          >
                            <XCircle size={14} />
                            Disconnect
                          </button>
                          <button
                            onClick={() => {
                              if (app.instructions?.destinationPage) {
                                window.location.href = app.instructions.destinationPage;
                              }
                            }}
                            className="px-3 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg text-sm font-medium transition-colors"
                            title="Open"
                          >
                            <ExternalLink size={14} />
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handleConnect(app)}
                          data-testid={`connect-${app.id}`}
                          disabled={connectingApp === app.id || app.connectType === 'coming_soon'}
                          className="flex-1 px-3 py-2 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1 disabled:opacity-50"
                          style={{ backgroundColor: app.connectType === 'coming_soon' ? '#6B7280' : app.color }}
                        >
                          {connectingApp === app.id ? (
                            <RefreshCw size={14} className="animate-spin" />
                          ) : app.connectType === 'coming_soon' ? (
                            'Coming Soon'
                          ) : (
                            <>
                              <LinkIcon size={14} />
                              Connect
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Connection Instructions Modal */}
      {showConnectionModal && selectedApp && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" 
          onClick={() => setShowConnectionModal(false)}
        >
          <div 
            className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-lg" 
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div 
                    className="p-3 rounded-lg"
                    style={{ backgroundColor: `${selectedApp.color}20` }}
                  >
                    <selectedApp.icon 
                      size={28} 
                      style={{ color: selectedApp.color }}
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{selectedApp.instructions?.title || `Connect ${selectedApp.name}`}</h3>
                    <p className="text-sm text-gray-400">Follow the steps below to connect</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowConnectionModal(false)}
                  className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
            </div>
            
            {/* Steps */}
            <div className="p-6">
              <div className="space-y-4">
                {selectedApp.instructions?.steps.map((step, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div 
                      className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                      style={{ backgroundColor: `${selectedApp.color}30`, color: selectedApp.color }}
                    >
                      {index + 1}
                    </div>
                    <p className="text-gray-300 text-sm pt-0.5">{step}</p>
                  </div>
                ))}
              </div>
              
              {/* Note */}
              {selectedApp.instructions?.note && (
                <div className="mt-6 p-4 bg-gray-800/50 border border-gray-700 rounded-lg flex items-start gap-3">
                  <Info size={18} className="text-blue-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-gray-400">{selectedApp.instructions.note}</p>
                </div>
              )}

              {/* Destination Page */}
              {selectedApp.instructions?.destinationPage && selectedApp.connected && (
                <div className="mt-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <p className="text-sm text-green-400">
                    ✓ View your {selectedApp.name} data at{' '}
                    <a 
                      href={selectedApp.instructions.destinationPage}
                      className="underline hover:text-green-300"
                    >
                      {selectedApp.instructions.destinationPage}
                    </a>
                  </p>
                </div>
              )}
            </div>
            
            {/* Actions */}
            <div className="p-6 border-t border-gray-700 flex gap-3">
              <button
                onClick={() => setShowConnectionModal(false)}
                className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              {selectedApp.connectType !== 'coming_soon' && (
                <button
                  onClick={handleConnectAction}
                  className="flex-1 px-4 py-3 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                  style={{ backgroundColor: selectedApp.color }}
                >
                  {selectedApp.connectType === 'oauth' ? (
                    <>
                      <ExternalLink size={18} />
                      Connect with {selectedApp.name.split(' ')[0]}
                    </>
                  ) : selectedApp.connectType === 'navigate' ? (
                    <>
                      <ArrowRight size={18} />
                      Go to {selectedApp.name.split(' ')[0]}
                    </>
                  ) : selectedApp.connectType === 'configured' ? (
                    <>
                      <ArrowRight size={18} />
                      View {selectedApp.name}
                    </>
                  ) : (
                    <>
                      <LinkIcon size={18} />
                      Connect
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* iCloud Credentials Modal */}
      {showICloudModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowICloudModal(false)}>
          <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Cloud className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Connect iCloud</h3>
                  <p className="text-sm text-gray-400">Enter your Apple ID credentials</p>
                </div>
              </div>
            </div>
            
            {/* Instructions */}
            <div className="p-4 bg-blue-500/5 border-b border-gray-700">
              <div className="flex items-start gap-2">
                <Info size={16} className="text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-gray-400">
                  <p className="font-medium text-blue-400 mb-1">How to get an App-Specific Password:</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Go to appleid.apple.com</li>
                    <li>Sign in → Security → App-Specific Passwords</li>
                    <li>Click "Generate Password"</li>
                    <li>Name it "Dashboard" and copy the password</li>
                  </ol>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Apple ID (Email)</label>
                <input
                  type="email"
                  value={icloudEmail}
                  onChange={(e) => setIcloudEmail(e.target.value)}
                  placeholder="your@icloud.com"
                  className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">App-Specific Password</label>
                <input
                  type="password"
                  value={icloudPassword}
                  onChange={(e) => setIcloudPassword(e.target.value)}
                  placeholder="xxxx-xxxx-xxxx-xxxx"
                  className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
                />
              </div>

              <a 
                href="https://appleid.apple.com/account/manage" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300"
              >
                <ExternalLink size={14} />
                Open Apple ID Settings
              </a>
            </div>
            
            <div className="p-6 border-t border-gray-700 flex gap-3">
              <button
                onClick={() => {
                  setShowICloudModal(false);
                  setIcloudEmail('');
                  setIcloudPassword('');
                }}
                className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleICloudConnect}
                className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                <LinkIcon size={18} />
                Connect
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Twilio Credentials Modal */}
      {showTwilioModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowTwilioModal(false)}>
          <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-red-500/20 rounded-lg">
                  {modalType === 'twilio_sms' ? (
                    <MessageCircle className="w-6 h-6 text-red-400" />
                  ) : (
                    <Phone className="w-6 h-6 text-red-400" />
                  )}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">
                    Connect Twilio {modalType === 'twilio_sms' ? 'SMS' : 'Voice'}
                  </h3>
                  <p className="text-sm text-gray-400">Enter your Twilio credentials</p>
                </div>
              </div>
            </div>
            
            {/* Instructions */}
            <div className="p-4 bg-red-500/5 border-b border-gray-700">
              <div className="flex items-start gap-2">
                <Info size={16} className="text-red-400 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-gray-400">
                  <p className="font-medium text-red-400 mb-1">Where to find your credentials:</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Go to console.twilio.com</li>
                    <li>Account SID is on the dashboard</li>
                    <li>Auth Token - click "Show" to reveal</li>
                    <li>Phone number - from Phone Numbers section</li>
                  </ol>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Account SID</label>
                <input
                  type="text"
                  value={twilioAccountSid}
                  onChange={(e) => setTwilioAccountSid(e.target.value)}
                  placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-red-500 focus:outline-none font-mono text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Auth Token</label>
                <input
                  type="password"
                  value={twilioAuthToken}
                  onChange={(e) => setTwilioAuthToken(e.target.value)}
                  placeholder="Your auth token"
                  className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-red-500 focus:outline-none"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Phone Number</label>
                <input
                  type="text"
                  value={twilioPhoneNumber}
                  onChange={(e) => setTwilioPhoneNumber(e.target.value)}
                  placeholder="+1234567890"
                  className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-red-500 focus:outline-none"
                />
              </div>
              
              <a 
                href="https://console.twilio.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-red-400 hover:text-red-300"
              >
                <ExternalLink size={14} />
                Open Twilio Console
              </a>
            </div>
            
            <div className="p-6 border-t border-gray-700 flex gap-3">
              <button
                onClick={() => {
                  setShowTwilioModal(false);
                  setTwilioAccountSid('');
                  setTwilioAuthToken('');
                  setTwilioPhoneNumber('');
                }}
                className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleTwilioConnect}
                className="flex-1 px-4 py-3 bg-[#F22F46] hover:bg-[#d62a3e] text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                <LinkIcon size={18} />
                Connect
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Nylas Add Email Account Modal */}
      {showNylasModal && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" 
          onClick={() => setShowNylasModal(false)}
        >
          <div 
            className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md" 
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-lg bg-blue-500/20">
                    <Mail size={28} className="text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Connect Email Account</h3>
                    <p className="text-sm text-gray-400">Add a new email account</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowNylasModal(false)}
                  className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <XCircle className="w-5 h-5 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Account Name (Optional)</label>
                <input
                  type="text"
                  value={newAccountName}
                  onChange={(e) => setNewAccountName(e.target.value)}
                  placeholder="e.g., Work Email, Personal"
                  className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
                />
                <p className="text-xs text-gray-500 mt-1">Give this account a friendly name for easy identification</p>
              </div>

              <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <p className="text-sm text-blue-300">
                  You'll be redirected to sign in with your email provider (Google, Microsoft, Yahoo, etc.)
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowNylasModal(false)}
                  className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConnectNylasAccount}
                  disabled={nylasConnecting}
                  className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="connect-nylas-btn"
                >
                  {nylasConnecting ? (
                    <RefreshCw size={18} className="animate-spin" />
                  ) : (
                    <LinkIcon size={18} />
                  )}
                  Connect
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyApps;
